import React, { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import { Link } from "react-router-dom";
import {
  FiUserCheck,
  FiTrash2,
  FiEye,
  FiShoppingCart,
  FiMail,
  FiPhone,
  FiUser,
  FiSearch,
  FiRefreshCw,
} from 'react-icons/fi';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import "../styles/UserList.css";
import "./ProductManagement.css";

// Same net-of-refunds calc as UserList.jsx - without it a refunded guest
// order still counted its full price toward "Omsetning".
function getNetOrderRevenue(order) {
  if (order.fullyRefunded) return 0;
  const orderItems = order.items || [];
  let refundedAmount = 0;
  orderItems.forEach(item => {
    const key = `${order.orderReference}_${item.name}`;
    const refundedQty = (order.refundedItems && order.refundedItems[key]) || 0;
    refundedAmount += refundedQty * (item.price || 0);
  });
  return (order.totalPrice || 0) - refundedAmount;
}

export default function GuestOrders() {
  const [guests, setGuests] = useState([]);
  const [orders, setOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: guestRows, error: guestsError } = await supabase
        .from("users").select("*").eq("is_guest", true);
      if (guestsError) throw guestsError;
      setGuests(guestRows || []);

      const guestIds = (guestRows || []).map(g => g.id);
      const { data: orderRows, error: ordersError } = guestIds.length
        ? await supabase.from("orders").select("*, order_items(*)").in("user_id", guestIds)
        : { data: [] };
      if (ordersError) throw ordersError;

      const ordersByGuest = {};
      (orderRows || []).forEach(order => {
        const shaped = {
          id: order.id,
          orderReference: order.order_reference,
          totalPrice: order.total_price,
          fullyRefunded: order.fully_refunded,
          refundedItems: order.refunded_items,
          items: (order.order_items || []).map(item => ({ name: item.name, price: item.price, quantity: item.quantity })),
        };
        if (!ordersByGuest[order.user_id]) ordersByGuest[order.user_id] = [];
        ordersByGuest[order.user_id].push(shaped);
      });
      setOrders(ordersByGuest);
    } catch (err) {
      setError("Kunne ikke hente gjestebestillinger: " + err.message);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  // Same as UserList.jsx's handleDelete - goes through the backend so orders,
  // tickets etc. under the guest are actually removed too, not just the row.
  const handleDelete = async (guestId, guestPhone) => {
    if (!window.confirm("Er du sikker på at du vil slette denne gjesten? Dette sletter all data permanent.")) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
      const response = await fetch(`${backendUrl}/api/users/${encodeURIComponent(guestPhone)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || `HTTP ${response.status}`);
      setGuests(guests.filter(g => g.id !== guestId));
      const newOrders = { ...orders };
      delete newOrders[guestId];
      setOrders(newOrders);
    } catch (err) {
      alert("Kunne ikke slette gjest: " + err.message);
    }
  };

  const filteredGuests = guests.filter(guest =>
    (guest.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (guest.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (guest.phone || '').toLowerCase().includes(searchTerm.toLowerCase())
  ).sort((a, b) => {
    switch (sortBy) {
      case 'name-asc': return (a.name || '').localeCompare(b.name || '');
      case 'name-desc': return (b.name || '').localeCompare(a.name || '');
      case 'date-desc': return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'date-asc': return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      default: return 0;
    }
  });

  const totalGuests = guests.length;
  const totalOrders = Object.values(orders).flat().length;
  const totalRevenue = Object.values(orders).flat().reduce((sum, order) => sum + getNetOrderRevenue(order), 0);

  if (loading) {
    return (
      <>
        <AdminDashboardNav />
        <div className="admin-container">
          <div className="loading-container">
            <FiRefreshCw className="loading-spinner" />
            <p>Laster gjester...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <AdminDashboardNav />
        <div className="admin-container">
          <div className="error-container">
            <p>Error: {error}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1 className="admin-title"><FiUserCheck style={{ marginRight: 8, verticalAlign: 'middle' }} />Gjestekjøp</h1>
            <p className="admin-subtitle">Kjøp gjort uten registrert konto - enkeltbilletter (golf/lek) kun</p>
          </div>
          <div className="admin-stats">
            <div className="stat-item"><FiUserCheck /><span>{totalGuests} Gjester</span></div>
            <div className="stat-item"><FiShoppingCart /><span>{totalOrders} Bestillinger</span></div>
            <div className="stat-item"><span>{totalRevenue.toFixed(0)} NOK Omsetning</span></div>
          </div>
        </div>

        <div className="pm-filters-section">
          <div className="pm-search-wrapper">
            <FiSearch className="pm-search-icon" />
            <input
              type="text"
              placeholder="Søk gjester etter navn, e-post eller telefon..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pm-search-input"
            />
          </div>
          <div className="pm-filter-group">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="pm-filter-select"
            >
              <option value="date-desc">Nyeste først</option>
              <option value="date-asc">Eldste først</option>
              <option value="name-asc">Navn A–Å</option>
              <option value="name-desc">Navn Å–A</option>
            </select>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={fetchData} disabled={loading}>
            <FiRefreshCw size={14} /> Oppdater
          </button>
        </div>

        <div className="card users-card">
          <div className="users-header">
            <h2><FiUserCheck style={{ marginRight: '8px', verticalAlign: 'middle' }} />Gjester</h2>
            <span className="users-count">{filteredGuests.length} gjester</span>
          </div>

          <div className="table-wrapper">
            <table className="users-table">
              <thead>
                <tr>
                  <th><div className="th-content"><FiUser /><span>Navn</span></div></th>
                  <th><div className="th-content"><FiMail /><span>E-post</span></div></th>
                  <th><div className="th-content"><FiPhone /><span>Telefon</span></div></th>
                  <th><div className="th-content"><FiShoppingCart /><span>Bestillinger</span></div></th>
                  <th>Totalt brukt</th>
                  <th>Registrert</th>
                  <th>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuests.map(guest => {
                  const guestOrders = orders[guest.id] || [];
                  const totalSpent = guestOrders.reduce((sum, order) => sum + getNetOrderRevenue(order), 0);

                  return (
                    <tr key={guest.id}>
                      <td>
                        <div className="user-info">
                          <div className="user-avatar">
                            <FiUser />
                          </div>
                          <span className="user-name">{guest.name || "Ingen navn"}</span>
                        </div>
                      </td>
                      <td>
                        <span className="user-email">{guest.email || "Ingen e-post"}</span>
                      </td>
                      <td>
                        <span className="user-phone">{guest.phone || "Ingen telefon"}</span>
                      </td>
                      <td>
                        {guestOrders.length > 0 ? (
                          <Link to={`/customer-orders/${guest.id}`} className="orders-link">
                            <FiShoppingCart />
                            <span>{guestOrders.length} bestillinger</span>
                          </Link>
                        ) : (
                          <span className="no-orders">Ingen bestillinger</span>
                        )}
                      </td>
                      <td>
                        <span className="total-spent">
                          {totalSpent > 0 ? `${totalSpent.toFixed(0)} NOK` : "0 NOK"}
                        </span>
                      </td>
                      <td>
                        <span className="join-date">
                          {guest.created_at
                            ? new Date(guest.created_at).toLocaleDateString('no-NO')
                            : "Ukjent"
                          }
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Link
                            to={`/customer-orders/${guest.id}`}
                            className="view-btn"
                            title="Se bestillinger"
                          >
                            <FiEye />
                          </Link>
                          <button
                            className="delete-btn"
                            onClick={() => handleDelete(guest.id, guest.phone)}
                            title="Slett gjest"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredGuests.length === 0 && searchTerm && (
            <div className="no-results">
              <p>Ingen gjester funnet som samsvarer med "{searchTerm}"</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
